import { Card, Typography, Statistic, Spin } from "antd";
import { useProvinceCountsFromFirebase } from "api/map/useProvinceCountsFromFirebase";
import { nepalProvincesData } from "./nepal-provinces.geojson";
import { Feature } from "geojson";
import { useState, useMemo, useEffect } from "react";

const { Title, Paragraph } = Typography;

// --- Constants & Configuration ---
const MAP_SCALE_X = 100;
const MAP_SCALE_Y = -120;
const MAP_OFFSET_X = -80 * MAP_SCALE_X;
const MAP_OFFSET_Y = 30.5 * -MAP_SCALE_Y;

// ADDED: A mapping object to fix spelling/spacing differences
const geoJsonNameToFirestoreKey: { [key: string]: string } = {
  "Madesh": "Madhesh",
  "Sudur Pashchim": "SudurPaschim",
};

const provinceColors: string[] = [
  "#A3D9A5", "#F6CBA1", "#8EC6E6", "#F8BBD0", "#FFD699", "#C4B6F1", "#9FD8CB",
];

const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
};

const coordinatesToPath = (coordinates: number[][][]) => {
  return coordinates[0].map((point, i) => {
    const [x, y] = point;
    const svgX = x * MAP_SCALE_X + MAP_OFFSET_X;
    const svgY = y * MAP_SCALE_Y + MAP_OFFSET_Y;
    return i === 0 ? `M ${svgX} ${svgY}` : `L ${svgX} ${svgY}`;
  }).join(" ") + " Z";
};

const scaleToSvg = (x: number, y: number): [number, number] => {
  return [x * MAP_SCALE_X + MAP_OFFSET_X, y * MAP_SCALE_Y + MAP_OFFSET_Y];
};

const getCentroid = (polygon: number[][]) => {
  const xSum = polygon.reduce((sum, p) => sum + p[0], 0);
  const ySum = polygon.reduce((sum, p) => sum + p[1], 0);
  return [xSum / polygon.length, ySum / polygon.length];
};

const AnimatedNumber = ({ value, duration = 400, ...textProps }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number | undefined;
    let animationFrameId: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      const easedPercentage = 1 - Math.pow(1 - percentage, 3);
      setCount(Math.floor(value * easedPercentage));

      if (progress < duration) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrameId);
  }, [value, duration]);

  return (
    <text {...textProps}>
      {count}
      <tspan fontSize={textProps.fontSize} dy="-0.1em">+</tspan>
    </text>
  );
};


const LandingMap = () => {
  const { data: provinceData, isError, isLoading } = useProvinceCountsFromFirebase();
  const [hoveredProvince, setHoveredProvince] = useState<string | null>(null);

  const { provinceFeatures } = useMemo(() => {
    const features = nepalProvincesData.features.map((feature: Feature, idx) => ({
      ...feature,
      properties: {
        ...feature.properties,
        color: provinceColors[idx] || "#ccc",
      },
    }));
    return { provinceFeatures: features };
  }, []);
  
  const hoveredFeature = useMemo(() => 
    hoveredProvince ? provinceFeatures.find(f => f.properties.Province === hoveredProvince) : null,
    [hoveredProvince, provinceFeatures]
  );

  if (isError) return null;

  return (
    <div className="bg-gray-100 min-h-screen flex flex-col items-center py-12 px-4">
      <Statistic
        value="2500+"
        suffix="Total Candidates from all over nepal"
        valueStyle={{ fontSize: "4rem", fontWeight: 700, color: "#22AD5C" }}
      />
      <Title level={2} style={{ marginTop: "16px", marginBottom: "8px" }}>
        Find Vetted Professionals Across Nepal
      </Title>
      <Paragraph type="secondary" style={{ maxWidth: "650px", textAlign: "center", marginBottom: "40px", fontSize: "1.2rem" }}>
        Explore our interactive talent map to see the distribution of skilled enumerators in every province.
      </Paragraph>

      <Card
        style={{
          width: "100%",
          maxWidth: "1100px",
          borderRadius: "12px",
          border: "3px solid #22AD5C",
          backgroundColor: "#F0F2F5",
          padding: "24px 32px 24px 48px",
          overflow: "hidden",
        }}
      >
        <div style={{ position: 'relative', width: '100%', height: '550px' }}>
          {isLoading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 10 }}>
              <Spin size="large" />
            </div>
          )}
          <svg width="100%" height="100%" viewBox="0 0 900 500" style={{ opacity: isLoading ? 0.5 : 1 }}>
            {provinceFeatures.map((feature) => {
              const geoProvinceName = feature.properties.Province;
              const isHovered = hoveredProvince === geoProvinceName;
              const [centX, centY] = scaleToSvg(...getCentroid(feature.geometry.coordinates[0]));

              return (
                <path
                  key={geoProvinceName}
                  d={coordinatesToPath(feature.geometry.coordinates)}
                  fill={feature.properties.color}
                  stroke={isHovered ? "#000" : "#555"}
                  strokeWidth={isHovered ? 2.5 : 1.2}
                  transform={isHovered ? `scale(1.05)` : "scale(1)"}
                  style={{
                    transformOrigin: `${centX}px ${centY}px`,
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={() => setHoveredProvince(geoProvinceName)}
                  onMouseLeave={() => setHoveredProvince(null)}
                />
              );
            })}
            {hoveredFeature && (() => {
              const geoProvinceName = hoveredFeature.properties.Province;
              
              // FIXED: Use the mapping object to get the correct key for Firestore
              const firestoreKey = geoJsonNameToFirestoreKey[geoProvinceName] || geoProvinceName;
              const count = provinceData?.[firestoreKey] ?? 0;
              
              const [centX, centY] = scaleToSvg(...getCentroid(hoveredFeature.geometry.coordinates[0]));
              return (
                <g style={{ pointerEvents: "none" }}>
                  <AnimatedNumber key={geoProvinceName} value={count} x={centX} y={centY - 12} fontSize="32" fontWeight="bold" fill="#1F2937" textAnchor="middle" alignmentBaseline="middle" style={{ filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.5))' }} />
                  <text x={centX} y={centY + 18} fontSize="14" fontWeight="500" fill="#374151" textAnchor="middle" alignmentBaseline="middle" style={{ filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.5))' }}>Candidates</text>
                </g>
              );
            })()}
          </svg>
        </div>

        <div style={{
          display: 'flex',
          overflowX: 'auto',
          paddingBottom: '10px',
          marginTop: '16px',
          scrollbarWidth: 'thin',
          scrollbarColor: '#22AD5C #F0F2F5',
        }}>
          <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '16px', padding: '0 24px' }}>
            {provinceFeatures.map((feature) => {
              const geoProvinceName = feature.properties.Province;
              const isHovered = hoveredProvince === geoProvinceName;
              const provinceColor = feature.properties.color;
              const rgbColor = hexToRgb(provinceColor);

              return (
                <div
                  key={`legend-${geoProvinceName}`}
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    transform: isHovered ? 'scale(1.1) skewX(-20deg)' : 'scale(1) skewX(-20deg)',
                    opacity: hoveredProvince && !isHovered ? 0.6 : 1,
                    border: `3px solid ${provinceColor}`,
                    backgroundColor: isHovered ? `rgba(${rgbColor}, 0.2)` : '#FFFFFF',
                    flexShrink: 0,
                  }}
                  onMouseEnter={() => setHoveredProvince(geoProvinceName)}
                  onMouseLeave={() => setHoveredProvince(null)}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '5px 12px',
                    transform: 'skewX(20deg)',
                  }}>
                    <div style={{
                      width: '25px',
                      height: '14px',
                      backgroundColor: provinceColor,
                      marginRight: '10px',
                    }}></div>
                    <span style={{ fontWeight: 500, color: '#374151', fontSize: '14px', whiteSpace: 'nowrap' }}>
                      {geoProvinceName}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default LandingMap;